import { useEffect, useState, useMemo } from "react";
import { Rx } from "@/types/rx";
import { Weather } from "@/types/weather";
import { UserDefaults } from "@/types/user";
import { Button, NumberInput, Card, CardBody, Divider, Chip } from "@heroui/react"
import { useParams } from "@tanstack/react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTemperatureArrowUp, faTemperatureArrowDown, faCloudRain, faWind, faCalendarDay } from "@fortawesome/free-solid-svg-icons";
import allNHorizontalImage from "@/assets/images/all_n_horizontal.png";
import ScenarioPlannerIcon from "../nav/ScenarioPlannerIcon";
import dayjs from "dayjs";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";

type SimulatedRx = {
  n_uptake_difference: number;
}

function SimulateRxCard({
  rxId,
  weather,
  averageN,
  applicationCost,
  nitrogenCost,
  cropPrice,
  onSimulate,
}: {
  rxId: string | number;
  weather: Weather;
  averageN: number;
  applicationCost: number;
  nitrogenCost: number;
  cropPrice: number;
  onSimulate?: () => void;
}) {
  const { formatUnit } = useI18n();
  const { t } = useTranslation("messages");
  const { useMutateRequest } = useRequest();
  const [nUptakeDifference, setNUptakeDifference] = useState<number | undefined>(undefined);
  const simulateRx = useMutateRequest<SimulatedRx>({
    url: `/prescriptions/${rxId}/simulate/`,
    method: "post",
    errorMessage: t("error.failed_to_simulate_rx", { date: dayjs(weather.date).format('ddd, MMM DD') }),
    successMessage: t("success.rx_simulated", { date: dayjs(weather.date).format('ddd, MMM DD') }),
    onSuccess: (data) => {
      setNUptakeDifference(data.n_uptake_difference);
    },
  });
  const returnOnInvestment = useMemo(() => {
    if (nUptakeDifference === undefined) return 0;
    const cost = applicationCost + (nitrogenCost * averageN);
    const revenue = (nUptakeDifference / 1.2) * cropPrice;
    return revenue - cost;
  }, [nUptakeDifference, applicationCost, nitrogenCost, averageN, cropPrice]);

  return (
    <Card className="w-full text-sm">
      <CardBody className="flex flex-row items-center justify-between px-4">
        <div className="flex flex-col items-center">
          <Chip
            color="primary"
            variant="flat"
            size="sm"
          >
            <FontAwesomeIcon icon={faCalendarDay} className="pr-1" />
            Date
          </Chip>
          <span className="text-sm font-semibold">{dayjs(weather.date).format('M/D')}</span>
        </div>
        <div className="flex flex-col items-center">
          <Chip
            color="primary"
            variant="flat"
            size="sm"
          >
            <FontAwesomeIcon icon={faTemperatureArrowUp} className="pr-1" />
            High
          </Chip>
          <span>{weather.max_temperature.toFixed()} {formatUnit("F", "C")}</span>
        </div>
        <div className="flex flex-col items-center">
          <Chip
            color="primary"
            variant="flat"
            size="sm"
          >
            <FontAwesomeIcon icon={faTemperatureArrowDown} className="pr-1" />
            Low
          </Chip>
          <span>{weather.min_temperature.toFixed()} {formatUnit("F", "C")}</span>
        </div>
        <div className="flex flex-col items-center">
          <Chip
            color="primary"
            variant="flat"
            size="sm"
          >
            <FontAwesomeIcon icon={faCloudRain} className="pr-1" />
            Precipitation
          </Chip>
          <span>{weather.precipitation.toFixed(2)} {formatUnit("in", "mm")}</span>
        </div>
        <div className="flex flex-col items-center">
          <Chip
            color="primary"
            variant="flat"
            size="sm"
          >
            <FontAwesomeIcon icon={faWind} className="pr-1" />
            Wind
          </Chip>
          <span>{weather.wind_speed?.toFixed()} {formatUnit("mph", "km_h")}</span>
        </div>
        <Divider orientation="vertical" className="h-10" />
        {nUptakeDifference === undefined ? (
          <div className="flex flex-col items-center">
            <Button
              color="primary"
              size="sm"
              className="h-10"
              isLoading={simulateRx.isPending}
              onPress={() => {
                simulateRx.mutate({
                  data: {
                    application_date: weather.date,
                  },
                });
                if (onSimulate) {
                  onSimulate();
                }
              }}
            >
              <ScenarioPlannerIcon color="white" />
              Simulate
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-row w-full justify-between items-center gap-2">
              <span className="text-sm font-semibold">N Uptake Change:</span>
              <Chip
                size="sm"
                color={nUptakeDifference > 0 ? "success" : "danger"}
              >
                {nUptakeDifference.toFixed(2)} {formatUnit("lbs", "kg")} N/{formatUnit("ac", "ha")}
              </Chip>
            </div>
            <div className="flex flex-row w-full justify-between items-center gap-2">
              <span className="text-sm font-semibold">Estimated ROI:</span>
              <Chip
                size="sm"
                color={returnOnInvestment > 0 ? "success" : "danger"}
              >
                $ {returnOnInvestment?.toFixed(2)} /{formatUnit("ac", "ha")}
              </Chip>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function SimulateRx() {
  const { formatUnit } = useI18n();
  const { user } = useAuth();
  const { useGetRequest, useMutateRequest } = useRequest();
  const { fieldId, rxId } = useParams({
    from: "/dashboard/fields/$fieldId/rx/$rxId",
  });
  const [averageN, setAverageN] = useState<number>(0);
  const [applicationCost, setApplicationCost] = useState<number>(0);
  const [nitrogenCost, setNitrogenCost] = useState<number>(0);
  const [cropPrice, setCropPrice] = useState<number>(0);
  const getRx = useGetRequest<Rx>({
    url: `/prescriptions/${rxId}/`,
    enabled: !!rxId,
  });
  useEffect(() => {
    if (getRx.data) {
      setAverageN(getRx.data.average_n_rate);
    }
  }, [getRx.data]);
  const getWeatherForecast = useGetRequest<Weather[]>({
    url: `/fields/${fieldId}/weather/`,
    params: {
      start_date: dayjs().format("YYYY-MM-DD"),
      end_date: dayjs().add(7, "day").format("YYYY-MM-DD"),
    },
  });
  const userDefaults = useGetRequest<UserDefaults>({
    url: `/users/${user?.id}/defaults/`,
    enabled: !!user?.id,
  });
  const updateUserDefaults = useMutateRequest<UserDefaults>({
    url: `/users/${user?.id}/defaults/`,
    method: "put",
  });
  useEffect(() => {
    if (userDefaults.data) {
      setApplicationCost(userDefaults.data.application_cost);
      setNitrogenCost(userDefaults.data.nitrogen_cost);
      setCropPrice(userDefaults.data.crop_price);
    }
  }, [userDefaults.data]);

  return (
    <div className="flex flex-col items-center justify-center w-full p-2 gap-1">
      <div className="flex flex-row items-center w-full justify-center">
        <img
          src={allNHorizontalImage}
          alt="All N Horizontal"
          className="h-10 object-cover"
        />
      </div>
      <div className="flex flex-row items-center gap-3 w-full">
        <NumberInput
          label={`Average N Rate (${formatUnit("lb_ac", "kg_ha").replace("/", " N/")})`}
          labelPlacement="outside"
          value={averageN}
          onValueChange={setAverageN}
          min={0}
          max={200}
          step={1}
          isDisabled
          hideStepper
        />
        <NumberInput
          label={`Application Cost ($/${formatUnit("ac", "ha")})`}
          labelPlacement="outside"
          value={applicationCost}
          onValueChange={setApplicationCost}
          min={0}
          formatOptions={{
            style: "currency",
            currency: "USD",
          }}
        />
      </div>
      <div className="flex flex-row items-center gap-3 w-full">
        <NumberInput
          label={`Nitrogen Cost ($/${formatUnit("lbs", "kg")} N)`}
          labelPlacement="outside"
          value={nitrogenCost}
          onValueChange={setNitrogenCost}
          min={0}
          formatOptions={{
            style: "currency",
            currency: "USD",
          }}
        />
        <NumberInput
          label="Crop Price ($/bu)"
          labelPlacement="outside"
          value={cropPrice}
          onValueChange={setCropPrice}
          min={0}
          formatOptions={{
            style: "currency",
            currency: "USD",
          }}
        />
      </div>
      <Divider className="w-full" />
      {
        getWeatherForecast.data?.map((weather) => (
          <SimulateRxCard
            rxId={rxId}
            weather={weather}
            averageN={averageN}
            applicationCost={applicationCost}
            nitrogenCost={nitrogenCost}
            cropPrice={cropPrice}
            onSimulate={() => {
              if (
                userDefaults.data &&
                (userDefaults.data.application_cost !== applicationCost ||
                  userDefaults.data.nitrogen_cost !== nitrogenCost ||
                  userDefaults.data.crop_price !== cropPrice)
              ) {
                updateUserDefaults.mutate({
                  data: {
                    application_cost: applicationCost,
                    nitrogen_cost: nitrogenCost,
                    crop_price: cropPrice,
                  }
                }, {
                  onSuccess: () => {
                    userDefaults.refetch();
                  }
                });
              }
            }}
          />
        ))
      }
    </div>
  );
}
export default SimulateRx;